import { UserDetails } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import dayjs from 'dayjs';
import { TaskCenterTypes } from '@frontend/api-task-center';
import { PortalUser } from '@frontend/auth-helpers';
import { IconName, IconProps } from '@frontend/icons';
import { statusSwitcherVariantsColor, FormValues } from '@frontend/design-system';
import { CreateTaskFormType } from '../types';

export const getFullName = (user?: UserDetails | PortalUser | Person) => {
  if (!user) return '';

  return `${user.firstName} ${user.lastName}`.trim();
};

export const getFulfilledValues = (values: FormValues<CreateTaskFormType>) => {
  const updatedValues: FormValues<CreateTaskFormType> = {};
  // Iterate through all form values and only include non-empty values
  Object.entries(values).forEach(([key, value]) => {
    // Check if value exists and is not empty
    if (
      value !== undefined &&
      value !== null &&
      // For strings, check if they're non-empty
      !(typeof value === 'string' && value === '') &&
      // For arrays, check if they're non-empty
      !(Array.isArray(value) && value.length === 0)
    ) {
      updatedValues[key as keyof CreateTaskFormType] = value;
    }
  });

  return updatedValues;
};

export const hasSameValues = (obj1: FormValues<CreateTaskFormType>, obj2: TaskCenterTypes.Task): boolean => {
  const keyMapping: Record<keyof FormValues<CreateTaskFormType>, keyof TaskCenterTypes.Task> = {
    title: 'title',
    description: 'description',
    locationId: 'locationId',
    patient: 'patient',
    dueDate: 'dueDate',
    assignee: 'assignee',
    status: 'status',
    priority: 'priority',
  };

  return Object.entries(obj1).every(([key, value]) => {
    const mappedKey = keyMapping[key as keyof FormValues<CreateTaskFormType>];
    if (key === 'patient') {
      return obj2.patient?.id && value ? obj2.patient?.id == value : true;
      // Checking if both the values are not present in the form and the task, and the type of the values mismatches
    } else if (typeof obj2[mappedKey] !== 'string' && value === '') return true;
    return obj2[mappedKey] === value;
  });
};

export const getStatusIconName = (status?: TaskCenterTypes.TaskStatus): { name: IconName; color?: string } => {
  if (status === TaskCenterTypes.TaskStatus.STATUS_COMPLETED) {
    return { name: 'check-small', color: statusSwitcherVariantsColor['success'].color };
  } else if (status === TaskCenterTypes.TaskStatus.STATUS_IN_PROGRESS) {
    return { name: 'in-progress-small', color: statusSwitcherVariantsColor['warning'].color };
  } else if (status === TaskCenterTypes.TaskStatus.STATUS_TODO) {
    return { name: 'pending-small', color: statusSwitcherVariantsColor['critical'].color };
  }
  return { name: 'pending-small', color: statusSwitcherVariantsColor['default'].color };
};

export const getPriorityIcon = (priority: TaskCenterTypes.PriorityType): IconProps => {
  if (priority === TaskCenterTypes.PriorityType.PRIORITY_HIGH) {
    return { name: 'priority-high', color: 'error', size: 16 };
  } else if (priority === TaskCenterTypes.PriorityType.PRIORITY_MEDIUM) {
    return { name: 'priority-medium', size: 16 };
  } else if (priority === TaskCenterTypes.PriorityType.PRIORITY_LOW) {
    return { name: 'priority-low', size: 16 };
  }
  return { name: 'no-priority', color: 'disabled', size: 16 };
};

export const formatTaskDate = (date?: string) => {
  if (!date) return '';
  return dayjs(date).format('MMMM D, YYYY');
};
